/** A 2D point in either world or screen space, depending on context: `[x, y]`. */
export type Point2 = readonly [x: number, y: number];

/** A straight segment between two points. */
export type Segment2 = readonly [Point2, Point2];

export const distance = (a: Point2, b: Point2): number =>
  Math.hypot(b[0] - a[0], b[1] - a[1]);

export const isSamePoint = (a: Point2, b: Point2, tolerance: number): boolean =>
  Math.abs(a[0] - b[0]) <= tolerance && Math.abs(a[1] - b[1]) <= tolerance;

/**
 * The eight 45° unit directions, anticlockwise from +X, exact so that a point
 * placed along one carries no rounding.
 */
export const OCTANT_DIRECTIONS: readonly Point2[] = [
  [1, 0],
  [Math.SQRT1_2, Math.SQRT1_2],
  [0, 1],
  [-Math.SQRT1_2, Math.SQRT1_2],
  [-1, 0],
  [-Math.SQRT1_2, -Math.SQRT1_2],
  [0, -1],
  [Math.SQRT1_2, -Math.SQRT1_2],
];

/** `target`, moved onto the nearest of the 45° rays out of `origin`. */
export const snapToOctant = (origin: Point2, target: Point2): Point2 => {
  const dx = target[0] - origin[0];
  const dy = target[1] - origin[1];
  const octant = Math.round(Math.atan2(dy, dx) / (Math.PI / 4));
  const count = OCTANT_DIRECTIONS.length;
  const [ux, uy] = OCTANT_DIRECTIONS[(octant + count) % count];
  const along = dx * ux + dy * uy;
  return [origin[0] + ux * along, origin[1] + uy * along];
};

/** Axis-aligned bounds: `[minX, minY, maxX, maxY]`. */
export type Aabb2 = readonly [
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
];
